<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { getProductSharingUrl } from '@/legacy/ts/productSharing'
import { isDesktop, isMobile } from '@/legacy/ts/checkDevice'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import {
	generateUniqueId,
	addOverlayListItem,
	removeOverlayListItem,
} from '@/legacy/ts/overlay'
import BsSearchbar from '@/legacy/components/Searchbar.vue'
import BsGoBack from '@/legacy/components/GoBack.vue'
import { capitalizeFirstLetter, cn } from '@/lib/utils'

const props = defineProps({
	placeholderText: {
		type: String,
		default: '',
	},
	isIndex: {
		type: Boolean,
		default: false,
	},
	queryString: {
		type: String,
	},
	queryStringType: {
		type: String,
	},
	text: {
		type: Object,
	},
	currentBannerTab: {
		type: String,
		default: '',
	},
	currentSecondaryTab: {
		type: String,
		default: '',
	},
	sortMode: {
		type: String,
	},
	currentWarehouseAlias: {
		type: String,
	},
	currentCategoryAlias: {
		type: String,
	},
})

const emit = defineEmits(['focus', 'blur'])
const searchKeyword = ref(props.queryString)
const isOverlayShow = ref(false)
const uniqueId = generateUniqueId()
const overlay = ref<Element | null>(null)
const searchInput = ref<HTMLElement | null>(null)
const searchForm = ref<HTMLElement | null>(null)
const searchBar = ref<HTMLElement | null>(null)
const closeSvg = ref('hamburger_close')
const isFocus = ref(false)

const inputName = computed(() => {
	if (searchKeyword.value?.startsWith('#')) {
		const matchText = searchKeyword.value.match(/#([^\s#]+)/gm)
		return matchText ? 'hashtag' : 'q'
	}
	return 'q'
})

function formSubmit() {
	createGAEvent('c_search_f_ps_index', { category: 'product_sharing' })
	if (searchKeyword.value === '') return

	createGAEvent('ps_searched', {
		category: 'product_sharing',
		label: searchKeyword.value,
	})
	createGAEvent('ps_active_user')
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.CommunityPostListingPage,
		button_name: 'Search Submitted',
		tab_name: `${capitalizeFirstLetter(
			props.currentBannerTab,
		)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
		sort_option: `${capitalizeFirstLetter(props.sortMode ?? '')}`,
		warehouse_filter: `${props.currentWarehouseAlias}`,
		category_filter: `${props.currentCategoryAlias}`,
		keyword: `${searchKeyword.value}`,
	})
	navigateToNextPage()
}

function navigateToNextPage() {
	if (!searchKeyword.value) return

	window.location.href = getProductSharingUrl('search', {
		name: inputName.value,
		value: encodeURIComponent(
			inputName.value === 'hashtag'
				? searchKeyword.value.split('#').slice(1).join(',')
				: searchKeyword.value,
		),
	})
}

function formFocus() {
	isFocus.value = true
	handleFormFocusStyle()
	nextTick(() => {
		searchInput.value?.getElementsByTagName('input')[0].focus()
	})
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.CommunityPostListingPage,
		button_name: 'Search',
		tab_name: `${capitalizeFirstLetter(
			props.currentBannerTab,
		)}-${capitalizeFirstLetter(props.currentSecondaryTab)}`,
		sort_option: `${capitalizeFirstLetter(props.sortMode ?? '')}`,
		warehouse_filter: `${props.currentWarehouseAlias}`,
		category_filter: `${props.currentCategoryAlias}`,
		keyword: `${searchKeyword.value}`,
	})
}

function handleFormFocusStyle() {
	if (!isMobile.value || !isFocus.value || !props.isIndex) return
	if (searchForm.value) {
		searchForm.value.style.zIndex = isDesktop.value ? '2' : '3'
	}

	if (searchBar.value) {
		searchBar.value.style.zIndex = '3'
	}

	if (isMobile.value) {
		addOverlayListItem(uniqueId)
		isOverlayShow.value = true
	}
}

function formBlur() {
	isFocus.value = false
	emit('blur')
	removeOverlayListItem(uniqueId)
	setTimeout(() => {
		if (isMobile.value && !isFocus.value) {
			isOverlayShow.value = false
			if (searchForm.value) {
				searchForm.value.style.zIndex = 'unset'
			}
			if (searchBar.value) {
				searchBar.value.style.zIndex = 'unset'
			}
		}
	}, 100)
}

function closeOverlay() {
	removeOverlayListItem(uniqueId)
	isOverlayShow.value = false
}
function goBack() {
	if (props.isIndex) {
		closeOverlay()
		return
	}
	const propsValue = props.queryString || ''
	window.location.href = getProductSharingUrl('recommended', {
		name: props.queryStringType,
		value: encodeURIComponent(propsValue),
	})
}

watch(
	() => isMobile.value,
	value => {
		if (value !== true) closeOverlay()
		if (isFocus.value) {
			formFocus()
		}
	},
	{ immediate: true },
)

defineExpose({
	formFocus,
})
</script>
<template>
	<div
		:class="
			cn(
				'bs-search-bar relative mx-auto my-0 flex w-full items-center justify-center p-0',
				{ 'w-full': isOverlayShow },
				{ 'absolute left-0 top-0': isMobile && isFocus && isIndex },
			)
		"
		ref="searchBar"
		v-cloak
	>
		<div
			:class="
				cn(
					'bs-search-bar-overlay absolute top-0 z-[1] hidden h-[250px] w-full shadow-[0_1px_1px_0_rgba(0,0,0,0.1),0_2px_1px_-1px_rgba(0,0,0,0.08),0_1px_3px_0_rgba(0,0,0,0.16)] before:fixed before:left-0 before:top-0 before:h-full before:w-full before:bg-[rgba(38,38,38,0.13)] after:absolute after:left-0 after:top-0 after:h-[250px] after:w-full after:bg-white',
					{ 'fixed left-0 right-0': isIndex },
					{
						'fixed flex after:fixed after:h-full': isOverlayShow,
					},
				)
			"
			ref="overlay"
		>
			<span
				class="bs-icon bs-icon--close right-[20px] top-[12px] z-[1] cursor-pointer"
				v-if="!isDesktop"
				@click="closeOverlay"
			></span>
		</div>
		<div
			:class="
				cn(
					'relative flex w-full flex-col justify-center p-0 mdp:gap-[48px] lgOld:gap-[60px]',
					{ 'm-[32px] w-[1204px]': !isIndex && !isMobile },
				)
			"
		>
			<div
				v-if="!isIndex && !isOverlayShow"
				class="z-[3] mx-16px my-12px flex items-center mdp:m-0"
				:class="{
					'typography-s1-emp': !isMobile,
					'typography-h5-str': isMobile,
				}"
			>
				<bs-go-back class="lgOld:left-0" @click="goBack">
					{{ '' }}
				</bs-go-back>
				<span class="cursor-pointer" @click="goBack">
					{{ isMobile ? 'Search' : text?.button.goback }}
				</span>
			</div>
			<div
				v-if="isOverlayShow"
				class="typography-h5-str z-[3] mb-16px mt-[-12px] flex items-center"
			>
				<bs-go-back @click="goBack">
					{{ '' }}
				</bs-go-back>
				<span>{{ text?.search }}</span>
			</div>
			<form
				:class="
					cn(
						'relative z-[unset] flex items-start justify-center',
						{ 'm-20px': isMobile && !isIndex },
						{ 'mt-16px': isMobile && isOverlayShow },
						{ 'justify-between': !isIndex && !isMobile },
					)
				"
				@submit.prevent="formSubmit"
				ref="searchForm"
			>
				<slot name="result" />
				<BsSearchbar
					:value="searchKeyword ? searchKeyword : ''"
					:placeholder="placeholderText"
					:focus="isFocus"
					:isIndex="props.isIndex"
					:currentBannerTab="props.currentBannerTab"
					:currentSecondaryTab="props.currentSecondaryTab"
					@focus="formFocus"
					@blur="formBlur"
					@update:value="searchKeyword = $event"
					required
				/>
			</form>
		</div>
	</div>
</template>
